import { useMemo, useCallback } from 'react';
import { Card, Chip, Icon, Link, Radio, Text } from '@nimbus-ds/components';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { RestrictionType } from '@tiendanube/common/src/enums/RestrictionType';
import { Stack } from 'commons/components';
import { useModal } from 'commons/hooks';
import { useGetCountry } from 'domains/Auth/hooks';
import {
  SelectedZonesInterface,
  SelectProvinceModal,
} from 'domains/Shipping/components';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { getRestrictionTypeOptions } from './utils';
import Range from '../Range';

interface PickupCoverageCardProps {
  restrictionType: RestrictionType;
  restrictionArea: SelectedZonesInterface[];
  range: string;
  errors: Partial<Record<string, string>>;
  setFieldValue: (name: string, value: any) => void;
}

function PickupCoverageCard({
  restrictionType,
  restrictionArea,
  range,
  errors,
  setFieldValue,
}: Readonly<PickupCoverageCardProps>): JSX.Element {
  const t = useTranslationShipping('deliveryMethods.pickupPoints.restriction');

  const country = useGetCountry();

  const restrictionTypeOptions = useMemo(
    () => getRestrictionTypeOptions(country),
    [country],
  );

  const [isOpenZonesModal, openZonesModal, closeZonesModal] = useModal();

  const onChangeRestrictionType = useCallback(
    (value: RestrictionType) => {
      if (value === RestrictionType.AREA && restrictionArea.length === 0) {
        openZonesModal();
      }

      setFieldValue('restrictionType', value);
    },
    [restrictionArea.length, setFieldValue, openZonesModal],
  );

  const onChangeZones = useCallback(
    (deliveryZones: SelectedZonesInterface[]) => {
      setFieldValue('restrictionArea', deliveryZones);
    },
    [setFieldValue],
  );

  const handleRemoveZone = useCallback(
    (provinceCode: string) => {
      if (restrictionArea && restrictionArea.length > 0) {
        const newDeliveryZones = restrictionArea.filter(
          (zone) => zone.provinceCode !== provinceCode,
        );
        onChangeZones(newDeliveryZones);
      }
    },
    [restrictionArea, onChangeZones],
  );

  return (
    <Card>
      <Card.Header title={t('title')} />
      <Card.Body>
        <Stack column align="flex-start" spacing="base">
          {restrictionTypeOptions.map((option) => (
            <Radio
              key={option.value}
              id={option.value}
              label={t(option.label)}
              name="restrictionType"
              value={option.value}
              onChange={() => onChangeRestrictionType(option.value)}
              as="radio"
              checked={restrictionType === option.value}
            />
          ))}
        </Stack>
      </Card.Body>
      {restrictionType === RestrictionType.DISTANCE && (
        <Range range={range} errors={errors} setFieldValue={setFieldValue} />
      )}
      {restrictionType === RestrictionType.AREA && (
        <Card.Footer>
          <Card>
            <Stack align="flex-start" column spacing="tight">
              <Text fontSize="highlight" color="currentColor">
                {t('byZones.description')}
              </Text>
              <Stack wrap>
                {restrictionArea?.map(
                  ({ provinceCode, provinceName, cities }) => (
                    <Chip
                      key={provinceCode}
                      id={provinceCode}
                      text={
                        (cities?.length ?? 0) > 0
                          ? `${provinceName} (${cities.length})`
                          : `${provinceName} (${t('byZones.allCities')})`
                      }
                      removable
                      onClick={() => handleRemoveZone(provinceCode)}
                    />
                  ),
                )}
              </Stack>
              <Link
                appearance="primary"
                onClick={openZonesModal}
                textDecoration="none"
              >
                <Icon color="currentColor" source={<PlusCircleIcon />} />
                {t('byZones.action')}
              </Link>
            </Stack>
          </Card>
        </Card.Footer>
      )}
      {isOpenZonesModal && (
        <SelectProvinceModal
          selectedZones={restrictionArea}
          isOpen={isOpenZonesModal}
          onClose={closeZonesModal}
          onChange={onChangeZones}
        />
      )}
    </Card>
  );
}

export default PickupCoverageCard;
